import { useState } from 'react';
import type { Command } from '@integrate/contracts';
import { Modal } from '../ui/Modal.js';

/** A scenario as the project payload carries it. */
export interface ScenarioRow {
  id: string;
  name: string;
  isDefault: boolean;
}

/*
  Where a second scenario comes from.

  Every one of these five commands has been implemented, authorised and tested on the server since P3,
  and none had a call site — so a project had exactly the scenarios its import created, which is one.
  Comparing two carve-out options is not a power feature on a separation engagement; it is most of the
  work, and until now it could only be done with `curl`.

  DUPLICATE IS THE DEFAULT ACTION, and that is a decision about what people actually do rather than a
  guess. The shipped app has no create at all — its "new scenario" button calls
  `duplicateCurrentScenario` (orgchart.html:2062) — because a scenario is an ALTERNATIVE to a design
  somebody has already built, and starting one from nothing throws away the perimeter, the heads and the
  transfers they would have kept. An empty create exists here too, deliberately added and kept second.

  NOTHING HERE IS A NEW WRITE PATH. Every button dispatches a command through the same endpoint as a
  bucket move, so a scenario created here is one entry in the change log with an inverse, exactly like
  everything else.
*/
export function ScenariosModal({
  scenarios,
  activeScenarioId,
  readOnly,
  isOwner,
  onDispatch,
  onClose,
}: {
  scenarios: readonly ScenarioRow[];
  /** The one this tab is looking at. Duplicating forks THIS design, not the default. */
  activeScenarioId: string;
  readOnly: boolean;
  /** `deleteScenario` is owner-only; the other four are not. */
  isOwner: boolean;
  onDispatch(command: Command): Promise<boolean>;
  onClose(): void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);

  const active = scenarios.find((s) => s.id === activeScenarioId);
  const last = scenarios.length <= 1;

  const run = (command: Command): void => {
    if (busy) return;
    setBusy(true);
    void onDispatch(command).finally(() => {
      setBusy(false);
      setRenaming(null);
      setConfirming(null);
    });
  };

  /*
    An id minted here, in the same shape the TSA and build-up registers use.

    The client generating it is what makes the command IDEMPOTENT-ish and the inverse exact: the server
    is told which scenario to create rather than inventing one and reporting it back, so `deleteScenario`
    of that same id is a real inverse. Time plus randomness rather than `crypto.randomUUID` because the
    rest of this client already does it that way and one convention beats two.
  */
  const mintId = (): string => `sc-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

  /** "Carve-out (2)" — the first free suffix, so two duplicates do not collide by name. */
  const copyName = (base: string): string => {
    const taken = new Set(scenarios.map((s) => s.name));
    if (!taken.has(`${base} (2)`)) return `${base} (2)`;
    for (let n = 3; ; n++) if (!taken.has(`${base} (${n})`)) return `${base} (${n})`;
  };

  return (
    <Modal
      testId="scenarios-modal"
      title="Scenarios"
      onClose={onClose}
    >
      <p className="muted small">
        A scenario is one complete design — perimeter, transfers, TSAs, build-ups, stranded plans and
        heads. The roster, the cost assumptions and the target ladder are the project&rsquo;s and are
        shared by all of them.
      </p>

      <div data-testid="scenario-list">
        {scenarios.map((sc) => (
          <div className="sp-row" key={sc.id} data-testid={`scenario-row-${sc.id}`}>
            {renaming === sc.id ? (
              <>
                <input
                  className="grow"
                  data-testid="scenario-rename-input"
                  value={draft}
                  autoFocus
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && draft.trim()) {
                      run({ type: 'renameScenario', scenarioId: sc.id, name: draft.trim() });
                    }
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                />
                <button
                  data-testid="scenario-rename-save"
                  disabled={busy || !draft.trim()}
                  onClick={() => run({ type: 'renameScenario', scenarioId: sc.id, name: draft.trim() })}
                >
                  Save
                </button>
              </>
            ) : (
              <>
                <span className="k grow">
                  {sc.name}
                  {sc.isDefault && (
                    <span className="pill" data-testid={`scenario-default-${sc.id}`}>
                      default
                    </span>
                  )}
                  {sc.id === activeScenarioId && (
                    <span className="muted small" data-testid={`scenario-active-${sc.id}`}>
                      {' '}
                      · you are here
                    </span>
                  )}
                </span>
                <span className="v">
                  <button
                    data-testid={`scenario-rename-${sc.id}`}
                    disabled={busy || readOnly}
                    onClick={() => {
                      setRenaming(sc.id);
                      setDraft(sc.name);
                    }}
                  >
                    Rename
                  </button>
                  {!sc.isDefault && (
                    <button
                      data-testid={`scenario-make-default-${sc.id}`}
                      disabled={busy || readOnly}
                      title="Which scenario opens when somebody opens this project"
                      onClick={() => run({ type: 'setDefaultScenario', scenarioId: sc.id })}
                    >
                      Make default
                    </button>
                  )}
                  {isOwner && (
                    <button
                      className="danger"
                      data-testid={`scenario-delete-${sc.id}`}
                      /*
                        The server refuses the last one — "At least one scenario is required" — so
                        this is predicting a refusal rather than inventing a rule.
                      */
                      disabled={busy || readOnly || last}
                      title={
                        last
                          ? 'A project must keep at least one scenario'
                          : 'Delete this design and everything in it'
                      }
                      onClick={() => setConfirming(sc.id)}
                    >
                      Delete
                    </button>
                  )}
                </span>
              </>
            )}
          </div>
        ))}
      </div>

      {confirming && (
        /*
          A named confirmation rather than a bare "are you sure". Deleting a scenario takes a whole
          design with it — every bucket, TSA, build-up and head in it — and the name is the only
          thing that tells somebody which design they are about to lose.
        */
        <div className="sp-row" data-testid="scenario-delete-confirm">
          <span className="k grow">
            Delete <strong>{scenarios.find((s) => s.id === confirming)?.name}</strong> and every
            decision in it? It is one entry in the change log, so it can be undone from there.
          </span>
          <span className="v">
            <button
              className="danger"
              data-testid="scenario-delete-confirmed"
              disabled={busy}
              onClick={() => run({ type: 'deleteScenario', scenarioId: confirming })}
            >
              Delete
            </button>
            <button data-testid="scenario-delete-cancel" onClick={() => setConfirming(null)}>
              Keep it
            </button>
          </span>
        </div>
      )}

      <div className="sp-row">
        <button
          className="primary"
          data-testid="scenario-duplicate"
          disabled={busy || readOnly || !active}
          title="Fork the design you are looking at, so the two can be compared"
          onClick={() =>
            active &&
            run({
              type: 'duplicateScenario',
              sourceScenarioId: active.id,
              scenarioId: mintId(),
              name: copyName(active.name),
            })
          }
        >
          Duplicate {active ? `“${active.name}”` : 'this scenario'}
        </button>
        <button
          data-testid="scenario-create"
          disabled={busy || readOnly}
          title="A scenario with no decisions in it at all"
          onClick={() =>
            run({ type: 'createScenario', scenarioId: mintId(), name: copyName('Scenario') })
          }
        >
          Start an empty one
        </button>
      </div>

      <p className="muted small">
        Duplicating copies the whole design. An empty scenario starts with nobody decided, which the
        perimeter rule will then sweep — useful for a clean comparison, and rarely what you want when
        a design already exists.
      </p>
    </Modal>
  );
}
